// src/components/NotificationBell.jsx
// Global, role-scoped TAT breach centre for the Navbar.
// Live polling · click-through to the stage page · per-page filter · severity pills.
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
    IconButton, Badge, Popover, Box, Typography, Tooltip,
    Snackbar, Alert, Stack, CircularProgress,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import NotificationsNoneRoundedIcon from '@mui/icons-material/NotificationsNoneRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import ScienceRoundedIcon from '@mui/icons-material/ScienceRounded';
import BiotechRoundedIcon from '@mui/icons-material/BiotechRounded';
import MemoryRoundedIcon from '@mui/icons-material/MemoryRounded';
import AssignmentRoundedIcon from '@mui/icons-material/AssignmentRounded';
import HubRoundedIcon from '@mui/icons-material/HubRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getApiUrl } from '../utils/api';

const POLL_MS = 60000;

// ── Font ─────────────────────────────────────────────────────────────────────
const ROBOTO = '"Roboto", "Helvetica", "Arial", sans-serif';

// ── Brand (header/badge) ─────────────────────────────────────────────────────
const BRAND = '#3d5191';
const BRAND_DARK = '#2f3f75';
const BRAND_LIGHT = '#5468b4';

const ROUTE = {
    total: '/sample-registration-list',
    sample_prep: '/sample-preparation-view',
    library_prep: '/Library-Preparation-view',
    sequencing: '/Sequencing-view',
    bioinformatics: '/Bioinformatics-view',
};
const STAGE_LABEL = {
    total: 'Sample Reg', sample_prep: 'Sample Prep', library_prep: 'Library',
    sequencing: 'Sequencing', bioinformatics: 'Bioinfo',
};
const STAGE_ORDER = ['sample_prep', 'library_prep', 'sequencing', 'bioinformatics', 'total'];

// distinct, well-separated stage colours + their own icons
const STAGE = {
    sample_prep: { color: '#2563eb', Icon: ScienceRoundedIcon },     // blue
    library_prep: { color: '#7c3aed', Icon: BiotechRoundedIcon },     // violet
    sequencing: { color: '#0d9488', Icon: MemoryRoundedIcon },      // teal
    bioinformatics: { color: '#db2777', Icon: HubRoundedIcon },         // pink
    total: { color: '#475569', Icon: AssignmentRoundedIcon },  // slate
};
const stageMeta = (s) => STAGE[s] || { color: BRAND, Icon: AssignmentRoundedIcon };

// severity scale (right-hand metric only) — clearly separate from stage colours
const sevColor = (d) => (d >= 30 ? '#dc2626' : d >= 8 ? '#ea580c' : '#d97706');

const agoText = (ts) => {
    if (!ts) return '';
    const s = Math.floor((Date.now() - ts) / 1000);
    if (s < 5) return 'just now';
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    return `${Math.floor(s / 3600)}h ago`;
};

export default function NotificationBell() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [anchorEl, setAnchorEl] = useState(null);
    const [toast, setToast] = useState(null);
    const [filter, setFilter] = useState('all');
    const [loading, setLoading] = useState(true);
    const [updatedAt, setUpdatedAt] = useState(null);
    const seenIds = useRef(new Set());
    const firstLoad = useRef(true);

    const load = useCallback(async () => {
        try {
            const res = await axios.get(getApiUrl('/notifications'));
            const list = res.data?.items || [];
            setItems(list);
            setUpdatedAt(Date.now());
            if (!firstLoad.current) {
                const fresh = list.filter((n) => !seenIds.current.has(n.id));
                if (fresh.length) {
                    setToast(fresh.length === 1 ? fresh[0].message
                        : `${fresh.length} new overdue projects need attention`);
                }
            }
            seenIds.current = new Set(list.map((n) => n.id));
            firstLoad.current = false;
        } catch (err) { /* silent — keep last known state */ }
        finally { setLoading(false); }
    }, []);

    useEffect(() => {
        load();
        const t = setInterval(load, POLL_MS);
        const onVis = () => { if (document.visibilityState === 'visible') load(); };
        document.addEventListener('visibilitychange', onVis);
        return () => { clearInterval(t); document.removeEventListener('visibilitychange', onVis); };
    }, [load]);

    const stageCounts = useMemo(() => {
        const c = {};
        for (const n of items) c[n.stage] = (c[n.stage] || 0) + 1;
        return c;
    }, [items]);

    const visible = filter === 'all' ? items : items.filter((n) => n.stage === filter);
    const count = items.length;
    const open = Boolean(anchorEl);

    const goTo = (n) => {
        setAnchorEl(null);
        navigate(ROUTE[n.stage] || ROUTE.total, {
            state: { projectId: n.projectId, search: n.projectId, fromNotification: true },
        });
    };

    // ── Filter pill ──────────────────────────────────────────────────────────
    const FilterChip = ({ id, label, num }) => {
        const active = filter === id;
        const c = id === 'all' ? BRAND : stageMeta(id).color;
        const dim = !num && !active;
        return (
            <Box
                onClick={() => setFilter(id)}
                sx={{
                    display: 'inline-flex', alignItems: 'center', gap: 0.6, cursor: 'pointer',
                    pl: 1.25, pr: (num > 0 || active) ? 0.5 : 1.25, py: 0.5, borderRadius: 99,
                    fontSize: '0.74rem', fontWeight: 600, whiteSpace: 'nowrap', userSelect: 'none',
                    transition: 'all .15s ease',
                    color: active ? '#fff' : dim ? '#aab0b8' : c,
                    background: active ? c : alpha(c, dim ? 0.05 : 0.10),
                    boxShadow: active ? `0 4px 12px -3px ${alpha(c, 0.55)}` : 'none',
                    '&:hover': { background: active ? c : alpha(c, 0.17), transform: 'translateY(-1px)' },
                }}
            >
                {label}
                {(num > 0 || active) && (
                    <Box component="span" sx={{
                        minWidth: 20, textAlign: 'center', fontSize: '0.68rem', fontWeight: 800, lineHeight: '20px',
                        px: 0.5, borderRadius: 99,
                        color: active ? c : '#fff',
                        backgroundColor: active ? '#fff' : alpha(c, 0.9),
                    }}>{num}</Box>
                )}
            </Box>
        );
    };

    return (
        <>
            <Tooltip title="TAT alerts">
                <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ color: '#fff' }}>
                    <Badge
                        badgeContent={count} max={999} color="error"
                        sx={{
                            '& .MuiBadge-badge': {
                                fontFamily: ROBOTO,
                                fontWeight: 700, fontSize: '0.62rem',
                                boxShadow: count ? `0 0 0 2px ${alpha('#d32f2f', 0.35)}` : 'none',
                                ...(count ? {
                                    '@keyframes tatpulse': {
                                        '0%': { boxShadow: `0 0 0 0 ${alpha('#d32f2f', 0.5)}` },
                                        '70%': { boxShadow: `0 0 0 6px ${alpha('#d32f2f', 0)}` },
                                        '100%': { boxShadow: `0 0 0 0 ${alpha('#d32f2f', 0)}` },
                                    },
                                    animation: 'tatpulse 2.4s infinite',
                                } : {}),
                            },
                        }}
                    >
                        <NotificationsNoneRoundedIcon />
                    </Badge>
                </IconButton>
            </Tooltip>

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{
                    sx: {
                        width: 480, maxWidth: '96vw', mt: 1.4, borderRadius: '20px', overflow: 'hidden',
                        boxShadow: '0 24px 64px -12px rgba(30,45,90,0.32)', border: '1px solid #eceef3',
                        backgroundColor: '#f6f7fa',
                        // ── Force Roboto on every element inside the panel ──
                        fontFamily: ROBOTO,
                        '& *': { fontFamily: 'inherit' },
                    },
                }}
            >
                {/* ── Header ── */}
                <Box sx={{
                    position: 'relative', px: 2.5, pt: 2.25, pb: 2.25,
                    background: `linear-gradient(135deg, ${BRAND_DARK} 0%, ${BRAND} 60%, ${BRAND_LIGHT} 100%)`,
                    color: '#fff', overflow: 'hidden',
                }}>
                    <Box sx={{ position: 'absolute', top: -46, right: -34, width: 168, height: 168, borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />
                    <Box sx={{ position: 'absolute', bottom: -56, left: -24, width: 120, height: 120, borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ position: 'relative' }}>
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                            <Box sx={{
                                width: 46, height: 46, borderRadius: '14px', display: 'grid', placeItems: 'center',
                                backgroundColor: 'rgba(255,255,255,0.16)', border: '1px solid rgba(255,255,255,0.25)',
                            }}>
                                <WarningAmberRoundedIcon sx={{ fontSize: 24 }} />
                            </Box>
                            <Box>
                                <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', lineHeight: 1.15, letterSpacing: 0.2 }}>
                                    TAT Alerts
                                </Typography>
                                <Typography sx={{ fontSize: '0.74rem', opacity: 0.8 }}>
                                    Projects past their turnaround time
                                </Typography>
                            </Box>
                        </Stack>
                        <Tooltip title={updatedAt ? `Updated ${agoText(updatedAt)}` : 'Refresh'}>
                            <IconButton size="small" onClick={() => { setLoading(true); load(); }}
                                sx={{
                                    color: '#fff', backgroundColor: 'rgba(255,255,255,0.14)',
                                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.24)' },
                                }}>
                                {loading ? <CircularProgress size={16} sx={{ color: '#fff' }} />
                                    : <RefreshRoundedIcon fontSize="small" />}
                            </IconButton>
                        </Tooltip>
                    </Stack>

                    <Stack direction="row" alignItems="baseline" spacing={0.75} sx={{ position: 'relative', mt: 2 }}>
                        <Typography sx={{ fontSize: '2.1rem', fontWeight: 800, lineHeight: 1 }}>{count}</Typography>
                        <Typography sx={{ fontSize: '0.82rem', opacity: 0.85, fontWeight: 600 }}>
                            {count === 1 ? 'project overdue' : 'projects overdue'}
                        </Typography>
                    </Stack>
                </Box>

                {/* ── Filter row ── */}
                <Box sx={{ px: 2, py: 1.5, display: 'flex', gap: 0.7, flexWrap: 'wrap', backgroundColor: '#f6f7fa' }}>
                    <FilterChip id="all" label="All" num={count} />
                    {STAGE_ORDER.map((s) => (
                        <FilterChip key={s} id={s} label={STAGE_LABEL[s]} num={stageCounts[s] || 0} />
                    ))}
                </Box>

                {/* ── List ── */}
                <Box sx={{
                    maxHeight: 440, overflowY: 'auto', px: 1.5, pb: 1.75,
                    '&::-webkit-scrollbar': { width: 8 },
                    '&::-webkit-scrollbar-thumb': { backgroundColor: '#dfe3e8', borderRadius: 8 },
                }}>
                    {visible.length === 0 ? (
                        <Box sx={{ px: 2, py: 6.5, textAlign: 'center' }}>
                            <Box sx={{
                                width: 64, height: 64, borderRadius: '50%', display: 'grid', placeItems: 'center',
                                backgroundColor: alpha('#2e7d32', 0.10), mx: 'auto', mb: 1.5,
                            }}>
                                <CheckCircleRoundedIcon sx={{ fontSize: 36, color: '#2e7d32' }} />
                            </Box>
                            <Typography sx={{ fontWeight: 700, color: '#37474f' }}>All clear here</Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>
                                Nothing overdue on this filter.
                            </Typography>
                        </Box>
                    ) : (
                        visible.map((n) => {
                            const { color: c, Icon: StageIcon } = stageMeta(n.stage);
                            const sc = sevColor(n.daysOverdue);
                            return (
                                <Box key={n.id} onClick={() => goTo(n)}
                                    sx={{
                                        position: 'relative',
                                        display: 'grid',
                                        gridTemplateColumns: '44px 1fr auto 22px',
                                        alignItems: 'center',
                                        columnGap: 1.5,
                                        p: 1.5, pl: 1.75, mb: 0.85, borderRadius: '14px', cursor: 'pointer',
                                        backgroundColor: '#fff', border: '1px solid #edeff3',
                                        boxShadow: '0 1px 2px rgba(16,24,40,0.04)',
                                        transition: 'transform .16s ease, box-shadow .16s ease, border-color .16s ease',
                                        overflow: 'hidden',
                                        '&:hover': {
                                            borderColor: alpha(c, 0.5),
                                            boxShadow: `0 10px 24px -10px ${alpha(c, 0.5)}`,
                                            transform: 'translateY(-2px)',
                                        },
                                        // stage-coloured left rail
                                        '&::before': {
                                            content: '""', position: 'absolute', left: 0, top: 0, bottom: 0,
                                            width: 4, backgroundColor: c,
                                        },
                                    }}>
                                    {/* stage icon (col 1) */}
                                    <Box sx={{
                                        width: 44, height: 44, borderRadius: '12px',
                                        display: 'grid', placeItems: 'center',
                                        background: `linear-gradient(135deg, ${alpha(c, 0.18)}, ${alpha(c, 0.08)})`,
                                        color: c,
                                    }}>
                                        <StageIcon sx={{ fontSize: 22 }} />
                                    </Box>

                                    {/* text (col 2) — project ID only */}
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography sx={{
                                            fontSize: '0.86rem', fontWeight: 700, color: '#1f2937', mb: 0.5,
                                            whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.25,
                                        }}>
                                            {n.projectId}
                                        </Typography>
                                        <Box component="span" sx={{
                                            display: 'inline-block',
                                            fontSize: '0.63rem', fontWeight: 700, color: c, px: 0.75, py: 0.2,
                                            borderRadius: 99, backgroundColor: alpha(c, 0.11),
                                            lineHeight: 1.4,
                                        }}>{n.stageLabel}</Box>
                                    </Box>

                                    {/* severity metric (col 3) — fixed alignment */}
                                    <Box sx={{
                                        textAlign: 'center', minWidth: 66,
                                        px: 1, py: 0.6, borderRadius: '12px',
                                        backgroundColor: alpha(sc, 0.12), border: `1px solid ${alpha(sc, 0.22)}`,
                                    }}>
                                        <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: sc, lineHeight: 1 }}>
                                            {n.daysOverdue}d
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.55rem', fontWeight: 700, color: sc, letterSpacing: 0.5, textTransform: 'uppercase', mt: 0.2 }}>
                                            overdue
                                        </Typography>
                                    </Box>

                                    {/* chevron (col 4) */}
                                    <ChevronRightRoundedIcon sx={{ color: '#c4c9d0', fontSize: 20 }} />
                                </Box>
                            );
                        })
                    )}
                </Box>
            </Popover>

            <Snackbar
                open={Boolean(toast)} autoHideDuration={6000} onClose={() => setToast(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert severity="warning" variant="filled" icon={<WarningAmberRoundedIcon />}
                    onClose={() => setToast(null)}
                    sx={{
                        maxWidth: 440, borderRadius: 2, fontWeight: 500,
                        fontFamily: ROBOTO,
                        '& *': { fontFamily: 'inherit' },
                        boxShadow: '0 8px 28px rgba(0,0,0,0.18)'
                    }}>
                    {toast}
                </Alert>
            </Snackbar>
        </>
    );
}